// services/api.js
// API Service Class - Synchronized with Mobile App
import { normalizeMessages, normalizeOfferList } from "../utils/offerNormalize";
import {
  API_BASE_URL,
  API_ENDPOINTS,
  API_ERROR_MESSAGES,
  DEFAULT_HEADERS,
  HTTP_STATUS,
  REQUEST_TIMEOUT,
  buildQueryString,
  buildUrl,
} from "../constants/api";
import { useAuthStore } from "../stores/authStore";
import { getAcceptLanguageHeader, extractLanguageCode, getLanguageFromStorage, getLanguageFromCookie, getLanguageFromBrowser, DEFAULT_LANGUAGE } from "../utils/languageUtils";

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.timeout = REQUEST_TIMEOUT;
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  async request(endpoint, options = {}, isRetry = false) {
    return new Promise((resolve, reject) => {
      const makeRequest = async () => {
        const { token, isAuthenticated } = useAuthStore.getState();

        // Destructure headers separately to prevent overwriting
        const { headers: optionHeaders, ...restOptions } = options;

        // Get current language for API requests (priority: localStorage > cookie > browser > default)
        let currentLang = getLanguageFromStorage() || getLanguageFromCookie() || getLanguageFromBrowser() || DEFAULT_LANGUAGE;
        currentLang = extractLanguageCode(currentLang);
        const acceptLanguage = getAcceptLanguageHeader(currentLang);

        // Build headers - only include Authorization token if user is authenticated
        // For public endpoints, don't send expired/invalid tokens
        const headers = {
          ...DEFAULT_HEADERS,
          'Accept-Language': acceptLanguage,
          ...(token && isAuthenticated && { Authorization: `Bearer ${token}` }),
          ...optionHeaders,
        };

        // Don't set Content-Type for FormData (let browser set it with boundary)
        if (options.body instanceof FormData) {
          delete headers["Content-Type"];
        }

        const config = {
          method: "GET",
          ...restOptions,
          headers,
        };

        const fullUrl = `${this.baseURL}${endpoint}`;

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), this.timeout);

          const response = await fetch(fullUrl, {
            ...config,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          // Handle 401 errors with token refresh
          // Only try to refresh if user is authenticated and endpoint requires auth
          if (response.status === HTTP_STATUS.UNAUTHORIZED && !isRetry) {
            const { isAuthenticated } = useAuthStore.getState();
            
            // Check if this is a public endpoint that doesn't require auth
            const publicEndpoints = [
              '/products',
              '/categories',
              '/products/suggestions',
              '/products/search',
              '/marketing-campaigns',
            ];
            const isPublicEndpoint = publicEndpoints.some(publicPath => endpoint.includes(publicPath));
            
            // If user is not authenticated, don't try to refresh token
            if (!isAuthenticated) {
              // For public endpoints, try without token
              if (isPublicEndpoint) {
                const retryOptions = {
                  ...options,
                  headers: {
                    ...DEFAULT_HEADERS,
                    'Accept-Language': acceptLanguage,
                    ...optionHeaders,
                  }
                };
                // Remove Authorization header for retry
                delete retryOptions.headers.Authorization;
                
                return this.request(endpoint, retryOptions, true)
                  .then(resolve)
                  .catch(reject);
              }
              
              // For protected endpoints when not authenticated, return error immediately
              // Don't try to refresh token or retry
              return this.handleErrorResponse(response, true)
                .then(() => resolve(null))
                .catch(reject);
            }
            
            // User is authenticated - try token refresh for protected endpoints
            if (this.isRefreshing) {
              return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject, endpoint, options });
              });
            }

            return this.handleTokenRefresh()
              .then(() => this.request(endpoint, options, true))
              .then(resolve)
              .catch(reject);
          }

          // Handle 403 errors that might be token-related (before handleErrorResponse)
          if (response.status === HTTP_STATUS.FORBIDDEN && !isRetry) {
            try {
              const errorData = await response.clone().json();
              const errorMessage = (errorData.message || '').toLowerCase();
              
              // Only try to refresh token if it's specifically a token error
              // Don't refresh for email verification, banned users, or other 403 errors
              if (errorMessage.includes('token') && (errorMessage.includes('invalid') || errorMessage.includes('expired'))) {
                if (!this.isRefreshing) {
                  return this.handleTokenRefresh()
                    .then(() => this.request(endpoint, options, true))
                    .then(resolve)
                    .catch(reject);
                } else {
                  return new Promise((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject, endpoint, options });
                  });
                }
              }
              // For other 403 errors (like email not verified), go to normal error handling
              // This will display the actual error message from the backend
            } catch {
              // If we can't parse the error, continue to normal error handling
            }
          }

          // Handle other error responses
          if (!response.ok) {
            await this.handleErrorResponse(response, isRetry);
          }

          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            resolve(await response.json());
          } else {
            resolve(await response.text());
          }
        } catch (error) {
          if (error.name === "AbortError") {
            // Only show timeout if it's actually a timeout, not if we already have an error message
            reject(new Error(API_ERROR_MESSAGES.TIMEOUT_ERROR));
          } else if (error.message && error.message !== API_ERROR_MESSAGES.TIMEOUT_ERROR) {
            // If we already have an error message (from handleErrorResponse), use it
            reject(error);
          } else {
            // Handle network errors gracefully
            try {
              this.handleRequestError(error);
            } catch (handledError) {
              reject(handledError);
            }
          }
        }
      };

      makeRequest().catch(reject);
    });
  }

  async handleTokenRefresh() {
    if (this.isRefreshing) {
      return Promise.resolve();
    }

    this.isRefreshing = true;

    try {
      const { isAuthenticated, refreshToken } = useAuthStore.getState();

      // Don't try to refresh if user is not authenticated
      if (!isAuthenticated) {
        this.isRefreshing = false;
        const error = new Error("Not authenticated");
        this.processFailedQueue(error);
        return Promise.reject(error);
      }

      // Don't try to refresh if there's no refresh token
      if (!refreshToken) {
        this.isRefreshing = false;
        const error = new Error("No refresh token available");
        this.processFailedQueue(error);
        useAuthStore.getState().logout(true);
        return Promise.reject(new Error(API_ERROR_MESSAGES.UNAUTHORIZED));
      }

      // Get refresh token from auth store and send in body
      const response = await fetch(
        `${this.baseURL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        {
          method: "POST",
          headers: DEFAULT_HEADERS,
          body: JSON.stringify({ refreshToken }),
        }
      );

      if (!response.ok) {
        // If refresh token is invalid/expired, logout user
        const { isAuthenticated: stillAuthenticated } = useAuthStore.getState();
        if (stillAuthenticated) {
          useAuthStore.getState().logout(true);
        }
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      if (data.status === "success") {
        // Update access token (refresh token is in httpOnly cookie, managed by backend)
        useAuthStore
          .getState()
          .updateToken(
            data.data.accessToken || data.data.token,
            null // Refresh token is in httpOnly cookie, don't store in state
          );
        this.processFailedQueue(null);
        return Promise.resolve();
      } else {
        // Refresh failed, logout user
        const { isAuthenticated: stillAuthenticated } = useAuthStore.getState();
        if (stillAuthenticated) {
          useAuthStore.getState().logout(true);
        }
        throw new Error(data.message || "Token refresh failed");
      }
    } catch (error) {
      this.processFailedQueue(error);
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        useAuthStore.getState().logout(true);
      }
      throw new Error(API_ERROR_MESSAGES.UNAUTHORIZED || "Session expired. Please login again.");
    } finally {
      this.isRefreshing = false;
    }
  }

  processFailedQueue(error) {
    this.failedQueue.forEach(({ resolve, reject, endpoint, options }) => {
      if (error) {
        reject(error);
      } else {
        this.request(endpoint, options, true).then(resolve).catch(reject);
      }
    });
    this.failedQueue = [];
  }

  async handleErrorResponse(response, isRetry = false) {
    // Ensure response exists and has a status
    if (!response) {
      throw new Error(API_ERROR_MESSAGES.SERVER_ERROR || 'An unknown error occurred');
    }

    const status = response.status || 500;
    let errorMessage = API_ERROR_MESSAGES.SERVER_ERROR || 'Server error. Please try again later.';

    try {
      const errorData = await response.json();
      // Always use the backend's error message if available (check both 'message' and 'error' fields)
      // Ensure errorMessage is a string and not undefined
      if (errorData && typeof errorData === 'object') {
        const backendMessage = errorData.message || errorData.error || errorData.msg;
        if (backendMessage && typeof backendMessage === 'string') {
          errorMessage = backendMessage;
        }
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // Ensure errorMessage is always a string
      if (!errorMessage || typeof errorMessage !== 'string') {
        errorMessage = API_ERROR_MESSAGES.SERVER_ERROR || 'Server error. Please try again later.';
      }
      } catch (parseError) {
      // If response is not JSON, use status-based message
      // errorMessage already has a default value
    }

    // Ensure errorMessage is always a valid string before throwing
    const finalErrorMessage = (errorMessage && typeof errorMessage === 'string') 
      ? errorMessage 
      : (API_ERROR_MESSAGES.SERVER_ERROR || 'Server error. Please try again later.');

    const throwWithStatus = (msg) => {
      const e = new Error(msg);
      e.status = status;
      throw e;
    };

    switch (status) {
      case HTTP_STATUS.UNAUTHORIZED:
        // Check if user is authenticated - if not, don't logout (they're just not logged in)
        const { isAuthenticated } = useAuthStore.getState();
        if (isRetry && isAuthenticated) {
          // Only logout if user was authenticated and retry failed
          useAuthStore.getState().logout(true);
          throwWithStatus(API_ERROR_MESSAGES.UNAUTHORIZED || 'Session expired. Please login again.');
        }
        // If not authenticated, just return a simple unauthorized message
        // Don't try to logout or refresh token
        if (!isAuthenticated) {
          throwWithStatus('Please login to access this resource');
        }
        // Use backend message if available, otherwise use default
        const unauthorizedMsg = (finalErrorMessage !== API_ERROR_MESSAGES.SERVER_ERROR) 
          ? finalErrorMessage 
          : 'Unauthorized';
        throwWithStatus(unauthorizedMsg);
        break;
      case HTTP_STATUS.FORBIDDEN:
        // For 403 errors, ALWAYS show the backend's message (email not verified, banned, etc.)
        // Don't try to refresh token here - that's handled before this function is called
        // This ensures messages like "Email not verified" are displayed correctly
        throwWithStatus(finalErrorMessage || API_ERROR_MESSAGES.FORBIDDEN || 'Access denied.');
        break;
      case HTTP_STATUS.NOT_FOUND:
        const notFoundMsg = (finalErrorMessage !== API_ERROR_MESSAGES.SERVER_ERROR) 
          ? finalErrorMessage 
          : (API_ERROR_MESSAGES.NOT_FOUND || 'Resource not found.');
        throwWithStatus(notFoundMsg);
        break;
      case HTTP_STATUS.BAD_REQUEST:
        throwWithStatus(finalErrorMessage || API_ERROR_MESSAGES.VALIDATION_ERROR || 'Please check your input and try again.');
        break;
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        // For 500 errors, show the backend's error message if available
        throwWithStatus(finalErrorMessage);
        break;
      default:
        // Always prefer backend's error message
        throwWithStatus(finalErrorMessage);
    }
  }

  handleRequestError(error) {
    // Handle network errors (QUIC, fetch failures, etc.)
    if (error.name === "TypeError" || 
        error.message.includes("Network") || 
        error.message.includes("Failed to fetch") ||
        error.message.includes("ERR_QUIC_PROTOCOL_ERROR") ||
        error.message.includes("QUIC")) {
      throw new Error(API_ERROR_MESSAGES.NETWORK_ERROR || "Network error. Please check your connection.");
    } else {
      throw error;
    }
  }

  // ==================== AUTH ENDPOINTS ====================
  async login(credentials) {
    return this.request(API_ENDPOINTS.AUTH.LOGIN, {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return this.request(API_ENDPOINTS.AUTH.SIGNUP, {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async checkEmail(email) {
    return this.request(API_ENDPOINTS.AUTH.CHECK_EMAIL, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async logout() {
    return this.request(API_ENDPOINTS.AUTH.LOGOUT, {
      method: "POST",
    });
  }

  async forgotPassword(email) {
    const resetOrigin =
      typeof window !== "undefined" ? window.location.origin : undefined;
    return this.request(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: "POST",
      body: JSON.stringify({ email, resetOrigin }),
    });
  }

  async resetPassword(email, code, newPassword) {
    return this.request(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: "POST",
      body: JSON.stringify({ email, code, password: newPassword }),
    });
  }

  async verifyEmail({ code, email }) {
    return this.request(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      method: "POST",
      body: JSON.stringify({ code, email }),
    });
  }

  async resendVerification(email) {
    return this.request(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async socialLogin(idToken, provider) {
    return this.request(API_ENDPOINTS.AUTH.SOCIAL_LOGIN, {
      method: "POST",
      body: JSON.stringify({ idToken, provider }),
    });
  }

  async firebaseSocialLogin(firebaseIdToken, providerHint) {
    return this.request(API_ENDPOINTS.AUTH.FIREBASE_SOCIAL_LOGIN, {
      method: "POST",
      body: JSON.stringify({ firebaseIdToken, providerHint }),
    });
  }

  // ==================== USER PROFILE ENDPOINTS ====================
  async getProfile() {
    return this.request(API_ENDPOINTS.USER.PROFILE);
  }

  // Update profile TEXT data (firstName, lastName, phoneNumber, etc.) - uses PATCH with JSON
  async updateProfile(userData) {
    return this.request(API_ENDPOINTS.USER.UPDATE_PROFILE, {
      method: "PATCH",  // PATCH for text data updates
      body: JSON.stringify(userData),
    });
  }

  async changePassword(oldPassword, newPassword) {
    return this.request(API_ENDPOINTS.USER.CHANGE_PASSWORD, {
      method: "PUT",
      body: JSON.stringify({ oldPassword, newPassword }),
    });
  }

  async uploadAvatar(formData) {
    return this.request(API_ENDPOINTS.USER.UPLOAD_AVATAR, {
      method: "POST",
      body: formData,
      // Note: Don't set Content-Type for FormData - browser will set it with boundary
    });
  }

  // Update user profile with FormData (for profile picture upload) - Same as mobile app
  async updateUserProfile(formData) {
    return this.request(API_ENDPOINTS.USER.UPDATE_PROFILE, {
      method: "PUT",
      body: formData,
      // Note: Don't set Content-Type for FormData - browser will set it with boundary
    });
  }

  async removeProfilePicture() {
    return this.request(API_ENDPOINTS.USER.REMOVE_PROFILE_PICTURE, {
      method: "DELETE",
    });
  }

  async deleteAccount() {
    return this.request(API_ENDPOINTS.USER.DELETE_ACCOUNT, {
      method: "DELETE",
    });
  }

  // ==================== PRODUCT ENDPOINTS ====================
  async getProducts(params = {}) {
    const queryString = buildQueryString(params);
    return this.request(`${API_ENDPOINTS.PRODUCTS.GET_ALL}${queryString}`);
  }

  async getProduct(id) {
    return this.request(buildUrl(API_ENDPOINTS.PRODUCTS.GET_BY_ID, { id }));
  }

  async searchProducts(query, params = {}) {
    // Backend expects 'search' parameter, not 'q'
    const queryString = buildQueryString({ search: query, ...params });
    return this.request(`${API_ENDPOINTS.PRODUCTS.SEARCH}${queryString}`);
  }

  async getSearchSuggestions(query) {
    const queryString = buildQueryString({ q: query });
    return this.request(`${API_ENDPOINTS.PRODUCTS.SUGGESTIONS}${queryString}`);
  }

  async getFeaturedProducts() {
    return this.request(API_ENDPOINTS.PRODUCTS.FEATURED);
  }

  async getFlashSales() {
    return this.request(API_ENDPOINTS.PRODUCTS.FLASH_SALES);
  }

  async getRecommendedProducts() {
    return this.request(API_ENDPOINTS.PRODUCTS.RECOMMENDED);
  }

  async getLastViewedProducts(limit = 20) {
    const queryString = buildQueryString({ limit });
    return this.request(`${API_ENDPOINTS.USER.LAST_VIEWS}${queryString}`);
  }

  async getProductsByCategory(categoryId, params = {}) {
    const queryString = buildQueryString(params);
    return this.request(
      `${buildUrl(API_ENDPOINTS.PRODUCTS.BY_CATEGORY, { categoryId })}${queryString}`
    );
  }

  async getProductsBySeller(sellerId, params = {}) {
    const queryString = buildQueryString(params);
    return this.request(
      `${buildUrl(API_ENDPOINTS.PRODUCTS.BY_SELLER, { sellerId })}${queryString}`
    );
  }

  // ==================== CATEGORY ENDPOINTS ====================
  async getCategories() {
    return this.request(API_ENDPOINTS.CATEGORIES.GET_ALL);
  }

  async getCategory(id) {
    return this.request(buildUrl(API_ENDPOINTS.CATEGORIES.GET_BY_ID, { id }));
  }

  async getSubcategories(categoryId) {
    return this.request(
      buildUrl(API_ENDPOINTS.CATEGORIES.GET_SUBCATEGORIES, { id: categoryId })
    );
  }

  // ==================== CART ENDPOINTS ====================
  async getCart() {
    return this.request(API_ENDPOINTS.CARTS.GET);
  }

  async addToCart(productId, quantity = 1, options = null) {
    const payload = { productId, quantity };
    if (options && typeof options === 'object') {
      if (options.sku) payload.sku = options.sku;
      if (options.variantId) payload.variantId = options.variantId;
      if (options.size) payload.size = options.size;
      if (options.color) payload.color = options.color;
    } else if (options) {
      payload.sku = options;
    }
    return this.request(API_ENDPOINTS.CARTS.ADD_ITEM, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async increaseCartItem(itemId) {
    return this.request(buildUrl(API_ENDPOINTS.CARTS.INCREASE, { itemId }), {
      method: "PATCH",
    });
  }

  async decreaseCartItem(itemId) {
    return this.request(buildUrl(API_ENDPOINTS.CARTS.DECREASE, { itemId }), {
      method: "PATCH",
    });
  }

  async updateCartItem(itemId, updates) {
    return this.request(buildUrl(API_ENDPOINTS.CARTS.UPDATE, { itemId }), {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async removeCartItem(itemId) {
    return this.request(buildUrl(API_ENDPOINTS.CARTS.REMOVE_ITEM, { itemId }), {
      method: "DELETE",
    });
  }

  async clearCart() {
    return this.request(API_ENDPOINTS.CARTS.CLEAR, {
      method: "DELETE",
    });
  }

  async acceptOffer(offerId) {
    return this.request(buildUrl(API_ENDPOINTS.OFFERS.ACCEPT, { offerId }), {
      method: "PUT",
    });
  }

  async rejectOffer(offerId, reason) {
    return this.request(buildUrl(API_ENDPOINTS.OFFERS.REJECT, { offerId }), {
      method: "PUT",
      body: JSON.stringify({ reason }),
    });
  }

  async addBidToCart(bidId, size, color, productId = null) {
    const body = { size, color };
    if (productId) {
      const productIdStr = String(productId).trim();
      if (productIdStr.length === 24 && /^[0-9a-fA-F]{24}$/.test(productIdStr)) {
        body.productId = productIdStr;
      }
    }
    return this.request(buildUrl(API_ENDPOINTS.OFFERS.ADD_TO_CART, { offerId: bidId }), {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // ==================== ORDER ENDPOINTS ====================
  async createOrder(orderData) {
    return this.request(API_ENDPOINTS.ORDERS.CREATE, {
      method: "POST",
      body: JSON.stringify(orderData),
    });
  }

  async getOrders(params = {}) {
    const queryString = buildQueryString(params);
    return this.request(`${API_ENDPOINTS.ORDERS.GET_ALL}${queryString}`);
  }

  async getOrder(id) {
    return this.request(buildUrl(API_ENDPOINTS.ORDERS.GET_BY_ID, { id }));
  }

  async cancelOrder(id, reason) {
    return this.request(buildUrl(API_ENDPOINTS.ORDERS.CANCEL, { id }), {
      method: "PUT",
      body: JSON.stringify(reason ? { reason } : {}),
    });
  }

  async deleteOrder(id) {
    return this.request(buildUrl(API_ENDPOINTS.ORDERS.GET_BY_ID, { id }), {
      method: "DELETE",
    });
  }

  async trackOrder(id) {
    return this.request(buildUrl(API_ENDPOINTS.ORDERS.TRACK, { id }));
  }

  // ==================== WISHLIST ENDPOINTS ====================
  async getWishlist() {
    return this.request(API_ENDPOINTS.WISHLIST.GET);
  }

  async addToWishlist(productId) {
    return this.request(API_ENDPOINTS.WISHLIST.ADD, {
      method: "POST",
      body: JSON.stringify({ productId }),
    });
  }

  async removeFromWishlist(productId) {
    return this.request(
      buildUrl(API_ENDPOINTS.WISHLIST.REMOVE, { productId }),
      { method: "DELETE" }
    );
  }

  async clearWishlist() {
    return this.request(API_ENDPOINTS.WISHLIST.CLEAR, {
      method: "DELETE",
    });
  }

  // ==================== ADDRESS ENDPOINTS ====================
  async getAddresses() {
    return this.request(API_ENDPOINTS.ADDRESS.GET_ALL);
  }

  async addAddress(addressData) {
    return this.request(API_ENDPOINTS.ADDRESS.ADD, {
      method: "POST",
      body: JSON.stringify(addressData),
    });
  }

  async updateAddress(id, addressData) {
    return this.request(buildUrl(API_ENDPOINTS.ADDRESS.UPDATE, { id }), {
      method: "PUT",
      body: JSON.stringify(addressData),
    });
  }

  async deleteAddress(id) {
    return this.request(buildUrl(API_ENDPOINTS.ADDRESS.DELETE, { id }), {
      method: "DELETE",
    });
  }

  async setDefaultAddress(id) {
    return this.request(buildUrl(API_ENDPOINTS.ADDRESS.SET_DEFAULT, { id }), {
      method: "PATCH",
    });
  }

  // ==================== REVIEW ENDPOINTS ====================
  async getProductReviews(productId) {
    return this.request(
      buildUrl(API_ENDPOINTS.PRODUCTS.REVIEWS, { id: productId })
    );
  }

  async createProductReview(productId, reviewData) {
    return this.request(
      buildUrl(API_ENDPOINTS.PRODUCTS.REVIEWS, { id: productId }),
      {
        method: "POST",
        body: JSON.stringify(reviewData),
      }
    );
  }

  async getAllReviews() {
    return this.request(API_ENDPOINTS.REVIEWS.GET_ALL);
  }

  // ==================== PAYMENT ENDPOINTS ====================
  async processPayment(paymentData) {
    return this.request(API_ENDPOINTS.PAYMENTS.PROCESS, {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  }

  async getPaymentOptions() {
    return this.request(API_ENDPOINTS.PAYMENT_OPTIONS.GET_ALL);
  }

  // ==================== BIDDING ENDPOINTS ====================
  async placeBid(productId, bidData) {
    const amount = bidData?.amount ?? bidData?.bidAmount;
    return this.request(buildUrl(API_ENDPOINTS.PRODUCTS.OFFER, { id: productId }), {
      method: "POST",
      body: JSON.stringify({ amount, message: bidData?.message }),
    });
  }

  async ensureStoreConversation(sellerId, text) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.BY_SELLER, { sellerId }),
      {
        method: "POST",
        body: JSON.stringify(text ? { text } : {}),
      }
    );
  }

  async ensureProductConversation(productId, text) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.BY_PRODUCT, { productId }),
      {
        method: "POST",
        body: JSON.stringify(text ? { text } : {}),
      }
    );
  }

  async attachProductToConversation(conversationId, productId) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.ATTACH_PRODUCT, {
        conversationId,
        productId,
      }),
      { method: "POST", body: JSON.stringify({}) }
    );
  }

  async getConversationDetail(conversationId) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.DETAIL, { conversationId })
    );
  }

  async getConversationMessages(conversationId) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.MESSAGES, { conversationId })
    );
  }

  async sendConversationMessage(conversationId, text, productId = null) {
    const body = { text };
    if (productId) body.productId = productId;
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.MESSAGES, { conversationId }),
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  async markConversationRead(conversationId) {
    return this.request(
      buildUrl(API_ENDPOINTS.CONVERSATIONS.READ, { conversationId }),
      { method: "PUT" }
    );
  }

  async getProductConversationMessages(productId) {
    const inquiryKey = `inquiry:${productId}`;
    try {
      const convResponse = await this.ensureProductConversation(productId);
      if (convResponse.status !== "success" || !convResponse.data) {
        return { status: "success", data: [], conversationId: null };
      }

      const conversationId = convResponse.data.id || convResponse.data._id;
      if (!conversationId) {
        return { status: "success", data: [], conversationId: null };
      }

      const fetchMessages = async () => {
        const messagesResponse = await this.getConversationMessages(conversationId);
        if (messagesResponse.status !== "success" || !messagesResponse.data) {
          return [];
        }
        const payload = messagesResponse.data;
        return Array.isArray(payload) ? payload : payload.messages || [];
      };

      let list = await fetchMessages();

      if (list.length === 0 && !this._seededInquiries?.has(inquiryKey)) {
        if (!this._seededInquiries) {
          this._seededInquiries = new Set();
        }
        this._seededInquiries.add(inquiryKey);
        try {
          await this.initiateProductInquiry(productId);
          list = await fetchMessages();
        } catch (inquiryErr) {
          console.warn('Product inquiry seed failed:', inquiryErr);
        }
      }

      try {
        await this.markConversationRead(conversationId);
      } catch (readErr) {
        console.warn('Mark conversation read failed:', readErr);
      }

      return {
        status: "success",
        data: normalizeMessages(list),
        conversationId,
        conversation: convResponse.data,
      };
    } catch (error) {
      if (error.status === 404 || error.message?.includes("not found")) {
        return { status: "success", data: [], conversationId: null };
      }
      throw error;
    }
  }

  /** Seeds PRODUCT_INQUIRY + welcome SYSTEM messages (canonical product route). */
  async initiateProductInquiry(productId) {
    return this.request(buildUrl(API_ENDPOINTS.PRODUCTS.INQUIRY, { id: productId }), {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async getMyBids(status, page = 1, limit = 10) {
    const params = { page, limit };
    if (status) params.status = status;
    const queryString = buildQueryString(params);
    try {
      const response = await this.request(`${API_ENDPOINTS.OFFERS.MY}${queryString}`);
      if (response.status === "success" && response.data) {
        const offers = normalizeOfferList(
          response.data.offers || response.data.bids || []
        );
        return {
          ...response,
          data: {
            ...response.data,
            offers,
            bids: offers,
          },
        };
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteBid() {
    throw new Error("Offers cannot be deleted. Wait for the seller to respond or place a new offer.");
  }

  /** @deprecated Use getProductConversationMessages */
  async getBidMessages(productId) {
    if (!productId) {
      return { status: "success", data: [] };
    }
    return this.getProductConversationMessages(productId);
  }

  async getConversations() {
    const response = await this.request(API_ENDPOINTS.CONVERSATIONS.LIST);
    if (response.status === 'success' && response.data) {
      return {
        ...response,
        data: Array.isArray(response.data)
          ? response.data
          : response.data.conversations || [],
      };
    }
    return response;
  }

  // ==================== SHIPPING ENDPOINTS ====================
  async getActiveShippingZones() {
    return this.request(API_ENDPOINTS.SHIPPING_ZONES.GET_ACTIVE);
  }

  async calculateShippingFee(neighborhoodCode) {
    return this.request(
      buildUrl(API_ENDPOINTS.SHIPPING_ZONES.CALCULATE, {
        neighborhoodCode: encodeURIComponent(neighborhoodCode),
      })
    );
  }

  // ==================== NOTIFICATION ENDPOINTS ====================
  async getNotifications() {
    return this.request(API_ENDPOINTS.NOTIFICATIONS.GET_ALL);
  }

  async getNotificationUnreadCount() {
    return this.request(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);
  }

  async clearAllNotifications() {
    return this.request(API_ENDPOINTS.NOTIFICATIONS.CLEAR_ALL, { method: "DELETE" });
  }

  async markNotificationAsRead(notificationId) {
    return this.request(
      buildUrl(API_ENDPOINTS.NOTIFICATIONS.MARK_READ, { id: notificationId }),
      { method: "PATCH" }
    );
  }

  async markAllNotificationsAsRead() {
    return this.request(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ, {
      method: "PATCH",
    });
  }

  async deleteNotification(notificationId) {
    return this.request(
      buildUrl(API_ENDPOINTS.NOTIFICATIONS.DELETE, { id: notificationId }),
      { method: "DELETE" }
    );
  }

  // ==================== SELLER ENDPOINTS ====================
  async getSeller(sellerId) {
    try {
      return await this.request(buildUrl(API_ENDPOINTS.SELLERS.GET_BY_ID, { sellerId }));
    } catch (err) {
      const is404 = err?.status === 404 ||
        err?.message?.includes("404") ||
        String(err?.message || "").toLowerCase().includes("not found");
      if (is404) {
        return {
          data: {
            name: "Boutique",
            logo: null,
            noOfRating: null,
            rating: null,
          },
        };
      }
      throw err;
    }
  }

  // ==================== BOUTIQUES (shop discovery) ====================
  async getBoutiques(params = {}) {
    const queryString = buildQueryString(params);
    return this.request(`${API_ENDPOINTS.BOUTIQUES.LIST}${queryString}`);
  }

  async getTopBoutiques(limit = 5) {
    try {
      return await this.request(
        `${API_ENDPOINTS.BOUTIQUES.TOP_PERFORMANCE}${buildQueryString({ limit })}`
      );
    } catch (err) {
      const is404 =
        err?.status === 404 ||
        String(err?.message || '').toLowerCase().includes('not found');
      if (is404) {
        return { data: [] };
      }
      throw err;
    }
  }

  async getBoutiqueProducts(boutiqueId, params = {}) {
    const queryString = buildQueryString(params);
    return this.request(
      `${buildUrl(API_ENDPOINTS.BOUTIQUES.PRODUCTS, { boutiqueId })}${queryString}`
    );
  }

  // ==================== MARKETING CAMPAIGNS ====================
  async getActiveCampaigns(params = {}) {
    const queryString = buildQueryString(params);
    return this.request(`${API_ENDPOINTS.MARKETING.ACTIVE}${queryString}`);
  }

  async getCampaignsByPlacement(placement, params = {}) {
    const queryString = buildQueryString(params);
    return this.request(
      `${buildUrl(API_ENDPOINTS.MARKETING.BY_PLACEMENT, { placement })}${queryString}`
    );
  }

  async validateCoupon(couponData) {
    return this.request(API_ENDPOINTS.MARKETING.VALIDATE_COUPON, {
      method: 'POST',
      body: JSON.stringify(couponData),
    });
  }
}

export const apiService = new ApiService();


